<template>
  <Popover ref="popover"
    :popoverAnchor="popoverAnchor"
    :showingPopover="showingPopover"
    :clickoutIgnoreElems="[popoverAnchor]"
    @update:showingPopover="closePopover"
  >
    <Keyboard ref="keyboard"
      :disableControlKeys="true"
      @ok="onKeyboardOk()"
      @cancel="onKeyboardCancel()"
      @keypress="onKeypress"
      @backspace="onBackspace"
    ></Keyboard>
  </Popover>
</template>

<script setup>
import { ref } from "vue";
import { useEventListener, useMutationObserver } from "@vueuse/core";
import Popover from "./Popover.vue";
import Keyboard from "./Keyboard.vue";

const popover = ref(null);
const popoverAnchor = ref(null);
const popoverAnchorParent = ref(null);
const showingPopover = ref(false);

let initialInputString = null;
useEventListener(document, "focusin", (event) => {
  let didFocusNewTextInput = (
    event.target.tagName == "INPUT" &&
    event.target.type === "text" &&
    event.target != popoverAnchor.value
  );
  if (didFocusNewTextInput) {
    let inputElem = event.target;
    initialInputString = inputElem.value;
    popoverAnchor.value = inputElem;
    popoverAnchorParent.value = inputElem.parentNode;
    showingPopover.value = true;
    inputElem.select();
  }
});

// Close the popover if the input element is removed from the DOM.
// - A mutation observer is used instead of listening for 'focusout', because the 'focusout' handler can't always 
//   know if the user clicked inside the keypad. For example, if a disabled keyboard button is clicked then 
//   'document.activeElement' would by the <body> element instead of the clicked button.
useMutationObserver(popoverAnchorParent, () => {
  if (!document.contains(popoverAnchor.value))
    closePopover();
}, { childList: true });

function onKeyboardOk() {
  let inputElem = popoverAnchor.value;
  inputElem.dispatchEvent(new CustomEvent("keyboard-popover-ok"), { bubbles: true, composed: true });
  closePopover();
}

function onKeyboardCancel() {
  // Restore the inputs origianl value if necessary.
  let inputElem = popoverAnchor.value;
  if (inputElem.value != initialInputString) {
    inputElem.value = initialInputString;
    inputElem.dispatchEvent(new Event("input"), { bubbles: true });
  }
  
  // Indicate to the component containing the input that the edit was cancelled.
  inputElem.dispatchEvent(new CustomEvent("keyboard-popover-cancel"), { bubbles: true, composed: true });
  
  closePopover();
}

function closePopover() {
  initialInputString = null;
  popoverAnchor.value = null;
  popoverAnchorParent.value = null;
  showingPopover.value = false;
}

function onKeypress(char) {
  let inputElem = popoverAnchor.value;
  let inputString = inputElem.value;
  let selectionStart = inputElem.selectionStart;
  let selectionEnd = inputElem.selectionEnd;
  let hasSelection = (selectionStart != selectionEnd);
  if (hasSelection)
    inputElem.value = inputString.slice(0, selectionStart) + char + inputString.slice(selectionEnd);
  else
    inputElem.value = inputString.slice(0, selectionStart) + char + inputString.slice(selectionStart);

  inputElem.setSelectionRange(selectionStart + 1, selectionStart + 1);

  // Dispatch an input event to notify the component containing the input of the change.
  inputElem.dispatchEvent(new Event("input", { bubbles: true }));
  
  // Return focus to the input.
  inputElem.focus();
}

function onBackspace() {
  let inputElem = popoverAnchor.value;
  let inputString = inputElem.value;
  let selectionStart = inputElem.selectionStart;
  let selectionEnd = inputElem.selectionEnd;
  let hasSelection = (selectionStart !== selectionEnd);

  if (hasSelection) {
    // Delete the selected text
    inputElem.value = inputString.slice(0, selectionStart) + inputString.slice(selectionEnd);
    inputElem.setSelectionRange(selectionStart, selectionStart);
  } else if (selectionStart > 0) {
    // Remove the character before the cursor
    inputElem.value = inputString.slice(0, selectionStart - 1) + inputString.slice(selectionEnd);
    inputElem.setSelectionRange(selectionStart - 1, selectionStart - 1);
  }

  // Dispatch an input event to notify the component containing the input of the change.
  inputElem.dispatchEvent(new Event("input", { bubbles: true }));

  // Return focus to the input.
  inputElem.focus();
}
</script>